import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/lib/select';

import './index.less';

import { getTranslateFunc } from '../../utils/stringUtil';
import IbSwitch from '../common/IbSwitch';
import { defaultApi } from '../../apis';
import { LARGE_PAGE_SIZE } from '../../constants';
import { alertsSelectorAdd } from '../../recoil';
import IbSelect from '../common/IbSelect';
import IbTypography from '../common/IbTypography';
import Switcher from '../common/Switcher';
import {
  ASRProviderModel,
  ASRProviderReferencePolicy,
  ASRProviderReferencePolicyStatus,
  ASRProviderReferenceSuiteOutput,
  ASRProviderReferenceSuiteOverlay,
  ASRProviderReferenceSuiteStatus,
} from '../../../gamma-api';

import { MAIN_CLASS_NAME, GROUP_CLASS_NAME, BLOCK_CLASS_NAME, TITLE_CLASS_NAME } from './const';

export interface IAsrProps {
  suite?: ASRProviderReferenceSuiteOutput | null;
  overlays?: ASRProviderReferenceSuiteOverlay[];
  onChange?: (suite: ASRProviderReferenceSuiteOutput) => void;
}

const Asr: React.FC<IAsrProps> = ({ suite, overlays, onChange }) => {
  const translate = getTranslateFunc('Recognizer selector: ASR:');

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [providers, setProviders] = useState<ASRProviderModel[]>([]);

  const [loading, setLoading] = useState(true);

  const editingDisabled = suite?.overlay !== ASRProviderReferenceSuiteOverlay.Overrides;
  const suiteDisabled = suite?.status !== ASRProviderReferencePolicyStatus.Enabled;

  const loadProviders = async () => {
    setLoading(true);

    try {
      const response = await defaultApi.searchAsrProviders(0, LARGE_PAGE_SIZE, false);
      setProviders(response.data.items || []);
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadProviders().finally();
  }, []);

  const onStatusToggle = (value: string | boolean) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      status: value ? ASRProviderReferenceSuiteStatus.Enabled : ASRProviderReferenceSuiteStatus.Disabled,
      policies: suite.policies.map((policy) => ({
        ...policy,
        status: value ? ASRProviderReferencePolicyStatus.Enabled : ASRProviderReferencePolicyStatus.Disabled,
      })),
    });
  };

  const onOverlayToggle = (value: string | boolean) => {
    if (!suite) {
      return;
    }

    const overlay = value as ASRProviderReferenceSuiteOverlay;

    onChange?.({
      ...suite,
      overlay,
      policies: suite.policies.map((policy) => ({
        ...policy,
        status:
          overlay === ASRProviderReferenceSuiteOverlay.Overrides
            ? ASRProviderReferencePolicyStatus.Enabled
            : ASRProviderReferencePolicyStatus.Disabled,
      })),
    });
  };

  const onProviderChange = (index: number) => (value: SelectValue) => {
    if (!suite) {
      return;
    }

    onChange?.({
      ...suite,
      policies: suite.policies.map((policy, idx) =>
        idx === index ? { ...policy, provider_id: value as string } : policy
      ),
    });
  };

  const overlaysOptions = (overlays || []).map((overlay) => ({
    value: overlay,
    label: translate(`Overlay: ${overlay}`),
  }));

  const providerOptions = providers.map((provider) => ({
    value: provider.asr_provider_id,
    label: `${provider.settings.type}: ${provider.name}`,
  }));

  const renderStatus = () => {
    return (
      <div className={GROUP_CLASS_NAME}>
        <div className={BLOCK_CLASS_NAME}>
          <div className={TITLE_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={suite?.status === ASRProviderReferencePolicyStatus.Enabled}
                size="small"
                onChange={onStatusToggle}
              />
              <IbTypography.Paragraph strong type={overlays !== undefined ? 'secondary' : 'normal'}>
                {translate('Status: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
            <IbTypography.Paragraph type="secondary">{translate('Status: Description')}</IbTypography.Paragraph>
          </div>
        </div>
      </div>
    );
  };

  const renderOverlay = () => {
    if (!overlaysOptions.length) {
      return null;
    }

    if (suiteDisabled) {
      return null;
    }

    return (
      <div className={GROUP_CLASS_NAME}>
        <div className={BLOCK_CLASS_NAME}>
          <IbTypography.Paragraph strong className={TITLE_CLASS_NAME} type="secondary">
            {translate('Overlay: Title')}
          </IbTypography.Paragraph>
          <Switcher options={overlaysOptions} value={suite?.overlay} onChange={onOverlayToggle} />
        </div>
      </div>
    );
  };

  const renderProvider = (policy: ASRProviderReferencePolicy, index: number) => {
    if (policy.status !== ASRProviderReferencePolicyStatus.Enabled) {
      return null;
    }

    return (
      <div className={BLOCK_CLASS_NAME}>
        <IbTypography.Paragraph strong className={TITLE_CLASS_NAME} disabled={editingDisabled} type="secondary">
          {translate('Provider: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          disabled={editingDisabled}
          loading={loading}
          options={providerOptions}
          value={policy.provider_id || undefined}
          onChange={onProviderChange(index)}
        />
      </div>
    );
  };

  const renderPolicy = (policy: ASRProviderReferencePolicy, index: number) => {
    return (
      <div key={index} className={GROUP_CLASS_NAME}>
        {renderProvider(policy, index)}
      </div>
    );
  };

  const renderPolicies = () => {
    if (suiteDisabled) {
      return null;
    }

    return suite?.policies.map(renderPolicy);
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderStatus()}
      {renderOverlay()}
      {renderPolicies()}
    </div>
  );
};

export default Asr;
